import { useLocation, Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { FileText, Shield, Scale, ChevronRight } from 'lucide-react';
import SEO from '@/components/SEO';

const legalPages = [
  { path: '/terms', label: 'Terms & Conditions', icon: Scale },
  { path: '/privacy', label: 'Privacy Policy', icon: Shield },
  { path: '/aml-cft', label: 'AML/CFT Policy', icon: FileText },
];

const legalContent = {
  '/terms': {
    title: 'Terms & Conditions',
    subtitle: 'Terms and Conditions of Trade',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'general',
        heading: '1. General',
        body: `These Terms and Conditions ("Terms") apply to all transactions between Southern Bullion Exchange Limited (NZBN: company number pending) ("SBE", "we", "us", "our") and our customers ("you", "your"). By placing an order, requesting a quote, accessing our website, or otherwise transacting with SBE, you acknowledge that you have read, understood, and agree to be bound by these Terms. We may update these Terms from time to time; the most current version will always be available on our website.`
      },
      {
        id: 'products',
        heading: '2. Products & Services',
        body: `SBE deals in physical precious metals including, but not limited to, gold bars, gold coins, silver bars, silver coins, platinum, and palladium products. All products sold by SBE are investment-grade and sourced from LBMA-accredited refiners or recognised government mints where applicable. We also offer a buy-back service, educational resources, and market data. Product images on our website are representative; minor variations in appearance may occur between individual items.`
      },
      {
        id: 'pricing',
        heading: '3. Pricing & Quotations',
        body: `All prices displayed on our website are indicative only and quoted in New Zealand Dollars (NZD). Indicative prices are based on the current international spot price plus applicable premiums and are subject to change without notice.\n\nLive quotations are available upon request and are valid for the time period expressly stated at the time the quote is issued (typically 10–15 minutes). Once a quote expires, a new quote must be requested. SBE reserves the right to withdraw or amend any quote prior to your acceptance.\n\nPrices include GST where applicable. Premiums vary by product type, size, availability, and market conditions. Premium schedules are available upon request.`
      },
      {
        id: 'orders',
        heading: '4. Orders & Payment',
        body: `Orders are confirmed only upon receipt of full payment. We accept the following payment methods:\n\n• Direct bank transfer (preferred)\n• Cash (for in-store transactions, subject to AML/CFT thresholds)\n• Stripe online payment (for selected products)\n\nAll orders are subject to product availability at the time payment is received. In the event of a stock shortfall, SBE will contact you to discuss alternatives or arrange a full refund. SBE reserves the right to cancel or modify orders if pricing errors occur due to technical faults, data feed errors, or human error.`
      },
      {
        id: 'delivery',
        heading: '5. Collection & Delivery',
        body: `Products can be collected in person from our Bishopdale store during business hours, or shipped via insured, tracked courier within New Zealand.\n\n• In-store collection: Available once payment has cleared. You will need to present photo ID upon collection.\n• Courier delivery: Shipping costs are calculated based on the value and weight of the order. All shipments are fully insured during transit. Delivery timeframes are estimates only and SBE accepts no liability for courier delays.\n\nRisk of loss or damage passes to the buyer upon: (a) collection from our premises, or (b) delivery to the address specified by the buyer. Title to goods passes upon receipt of full payment.`
      },
      {
        id: 'buyback',
        heading: '6. Buy-Back & Sell-to-Us',
        body: `SBE offers a buy-back service for precious metals products, including items not originally purchased from us. Buy-back prices are based on the current international spot price less an applicable spread, which varies by product type and condition.\n\nFor buy-back transactions:\n• Products must be presented in person at our Bishopdale store or sent via insured post.\n• All items will be inspected, tested, and weighed before a final offer is made.\n• SBE reserves the right to decline any buy-back at its sole discretion.\n• Payment for buy-back transactions is made via bank transfer within 1–3 business days of acceptance.\n• AML/CFT customer due diligence requirements apply to all sell-to-us transactions.`
      },
      {
        id: 'cdd',
        heading: '7. Customer Due Diligence',
        body: `In accordance with New Zealand's Anti-Money Laundering and Countering Financing of Terrorism Act 2009, SBE is a reporting entity and is required to verify the identity of all customers before conducting transactions. We may request identification documents, proof of address, and information regarding the source of funds. Transactions may be delayed or declined if satisfactory identification cannot be provided. Please refer to our separate AML/CFT Policy for full details.`
      },
      {
        id: 'disclaimer',
        heading: '8. Financial Advice Disclaimer',
        body: `SBE does not provide personalised financial advice. All information provided on our website, in our educational materials, or by our staff is general in nature and should not be relied upon as a substitute for professional financial advice. The value of precious metals can go down as well as up. Past performance is not indicative of future results. We strongly recommend that you seek independent financial advice before making investment decisions.`
      },
      {
        id: 'liability',
        heading: '9. Limitation of Liability',
        body: `To the maximum extent permitted by law, SBE's total liability in connection with any transaction shall not exceed the purchase price of the products in question. SBE shall not be liable for any indirect, incidental, consequential, or special damages, including but not limited to lost profits, loss of data, or business interruption. Nothing in these Terms excludes or limits liability that cannot be lawfully excluded under the Consumer Guarantees Act 1993 or other applicable New Zealand legislation.`
      },
      {
        id: 'ip',
        heading: '10. Intellectual Property',
        body: `All content on the SBE website, including text, images, logos, design elements, and software, is the property of Southern Bullion Exchange Limited or its licensors and is protected by New Zealand and international copyright and trademark laws. You may not reproduce, distribute, or create derivative works from our content without our express written permission.`
      },
      {
        id: 'disputes',
        heading: '11. Disputes & Governing Law',
        body: `These Terms are governed by and construed in accordance with the laws of New Zealand. Any dispute arising out of or in connection with these Terms shall first be attempted to be resolved through good faith negotiation. If unresolved, the dispute shall be subject to the exclusive jurisdiction of the New Zealand courts. Nothing in these Terms affects your rights under the Consumer Guarantees Act 1993 or the Fair Trading Act 1986.`
      },
      {
        id: 'contact',
        heading: '12. Contact Us',
        body: `Southern Bullion Exchange Limited\nShop 9 Bishopdale Court\nBishopdale 8053, Christchurch\nNew Zealand\n\nPhone: 021 153 1763 / (03) 359 6060\nEmail: southernbullionnz@gmail.com\nWebsite: www.southernbullionex.co.nz`
      },
    ]
  },
  '/privacy': {
    title: 'Privacy Policy',
    subtitle: 'How We Collect, Use, and Protect Your Information',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'introduction',
        heading: '1. Introduction',
        body: `Southern Bullion Exchange Limited ("SBE", "we", "us", "our") is committed to protecting your privacy and handling your personal information responsibly. This Privacy Policy explains how we collect, use, store, disclose, and protect your personal information in accordance with the Privacy Act 2020 (New Zealand) and the Information Privacy Principles ("IPPs") contained within it.\n\nBy using our website, visiting our store, or transacting with us, you consent to the collection and use of your information as described in this policy.`
      },
      {
        id: 'collection',
        heading: '2. Information We Collect',
        body: `We collect personal information that is necessary for us to provide our products and services, and to comply with our legal obligations. This may include:\n\n• Full name, date of birth, and residential address\n• Contact details (phone number, email address)\n• Government-issued identification documents (passport, driver's licence, firearms licence)\n• Proof of address documentation\n• Bank account details for payment processing\n• Transaction history and purchase records\n• Source of funds/wealth information (as required by AML/CFT legislation)\n• Correspondence and communications with us\n• Website usage data (IP address, browser type, pages visited)\n• Newsletter subscription preferences\n\nWe collect information directly from you, from publicly available sources, and through our website's analytics and cookies.`
      },
      {
        id: 'use',
        heading: '3. How We Use Your Information',
        body: `We use your personal information for the following purposes:\n\n• Processing and fulfilling your orders and transactions\n• Providing quotes and responding to enquiries\n• Conducting customer due diligence as required by the AML/CFT Act 2009\n• Complying with our legal and regulatory obligations\n• Communicating with you about your orders, our products, and services\n• Sending market updates, newsletters, and promotional material (with your consent)\n• Improving our website, products, and services\n• Preventing fraud and ensuring the security of our business\n• Maintaining accurate business records\n• Resolving disputes and enforcing our Terms and Conditions`
      },
      {
        id: 'disclosure',
        heading: '4. Disclosure of Information',
        body: `We may disclose your personal information to:\n\n• New Zealand Police Financial Intelligence Unit, as required by AML/CFT legislation\n• Government agencies and regulators, as required by law\n• Our professional advisors (accountants, lawyers) under obligations of confidentiality\n• Payment processors and banking partners to facilitate transactions\n• Courier and logistics providers for delivery of products\n• IT service providers who assist in operating our website and systems\n\nWe will not sell, rent, or trade your personal information to third parties for their marketing purposes. Any third-party service providers who access your information are bound by confidentiality obligations.`
      },
      {
        id: 'storage',
        heading: '5. Storage & Security',
        body: `We take reasonable steps to protect your personal information from unauthorised access, use, modification, or disclosure. Our security measures include:\n\n• Encrypted data transmission (SSL/TLS) on our website\n• Secure physical storage of identification documents\n• Access controls limiting staff access to personal information on a need-to-know basis\n• Regular security reviews of our systems and processes\n\nPersonal information is stored in New Zealand and may be processed using cloud-based services with servers located in Australia or New Zealand. We retain personal information only for as long as necessary for the purposes for which it was collected, or as required by law (AML/CFT records must be retained for a minimum of 5 years after the business relationship ends).`
      },
      {
        id: 'cookies',
        heading: '6. Cookies & Website Analytics',
        body: `Our website uses cookies and similar technologies to improve your browsing experience and to analyse website traffic. Cookies are small text files stored on your device. We use:\n\n• Essential cookies: Required for the website to function properly\n• Analytics cookies: Help us understand how visitors use our website\n• Preference cookies: Remember your settings and preferences\n\nYou can manage cookie preferences through your browser settings. Disabling cookies may affect the functionality of our website.`
      },
      {
        id: 'rights',
        heading: '7. Your Rights',
        body: `Under the Privacy Act 2020, you have the right to:\n\n• Access: Request a copy of the personal information we hold about you\n• Correction: Request correction of any inaccurate or incomplete information\n• Complaint: Make a complaint to the Office of the Privacy Commissioner if you believe your privacy has been breached\n\nTo exercise these rights, please contact us using the details below. We will respond to your request within 20 working days. In some cases, we may need to verify your identity before processing a request. There may be circumstances where we are unable to provide access or make corrections, for example where required by law or where doing so would compromise an ongoing investigation.`
      },
      {
        id: 'changes',
        heading: '8. Changes to This Policy',
        body: `We may update this Privacy Policy from time to time to reflect changes in our practices or applicable legislation. The most current version will always be available on our website with the date of the last update. We encourage you to review this policy periodically.`
      },
      {
        id: 'contact',
        heading: '9. Contact Us',
        body: `For privacy-related enquiries, access requests, or complaints:\n\nPrivacy Officer\nSouthern Bullion Exchange Limited\nShop 9 Bishopdale Court\nBishopdale 8053, Christchurch\nNew Zealand\n\nPhone: 021 153 1763 / (03) 359 6060\nEmail: southernbullionnz@gmail.com\n\nYou may also contact the Office of the Privacy Commissioner:\nwww.privacy.org.nz\nPhone: 0800 803 909`
      },
    ]
  },
  '/aml-cft': {
    title: 'AML/CFT Policy',
    subtitle: 'Anti-Money Laundering & Countering Financing of Terrorism',
    lastUpdated: 'February 2026',
    sections: [
      {
        id: 'commitment',
        heading: '1. Our Commitment',
        body: `Southern Bullion Exchange Limited ("SBE") is a reporting entity under the Anti-Money Laundering and Countering Financing of Terrorism Act 2009 ("AML/CFT Act") as a dealer in precious metals. We are committed to detecting and preventing money laundering, terrorism financing, and other financial crimes.\n\nWe maintain a comprehensive AML/CFT compliance programme that includes risk assessments, customer due diligence procedures, transaction monitoring, suspicious activity reporting, record keeping, and staff training. Our programme is reviewed and updated regularly to ensure ongoing compliance.`
      },
      {
        id: 'obligations',
        heading: '2. Our Legal Obligations',
        body: `As a reporting entity under the AML/CFT Act, SBE is required to:\n\n• Conduct risk assessments of our business and customers\n• Implement and maintain an AML/CFT compliance programme\n• Conduct customer due diligence (CDD) on all customers\n• Monitor transactions for suspicious activity\n• Report suspicious transactions to the New Zealand Police Financial Intelligence Unit\n• Report prescribed transactions (cash transactions of NZD 10,000 or more, or equivalent)\n• Maintain records of CDD information and transactions\n• Appoint a compliance officer responsible for AML/CFT compliance\n• Provide AML/CFT training to all relevant staff\n• Conduct independent audits of our compliance programme`
      },
      {
        id: 'cdd',
        heading: '3. Customer Due Diligence (CDD)',
        body: `We are required by law to verify the identity of all customers before conducting transactions. CDD must be completed before or, in certain circumstances, during the course of establishing a business relationship.\n\nThe level of CDD applied depends on the assessed risk of the customer and the nature of the transaction.`
      },
      {
        id: 'standard-cdd',
        heading: '3.1 Standard CDD',
        body: `For all customers, we require:\n\n• Full legal name\n• Date of birth\n• Current residential address\n• One form of valid government-issued photo identification:\n  — New Zealand passport\n  — New Zealand driver's licence\n  — Overseas passport\n  — New Zealand firearms licence\n• One form of proof of current address (dated within 3 months):\n  — Utility bill (power, gas, water, internet)\n  — Bank or credit card statement\n  — Government correspondence (IRD, council rates)\n  — Tenancy agreement`
      },
      {
        id: 'enhanced-cdd',
        heading: '3.2 Enhanced Due Diligence (EDD)',
        body: `Enhanced due diligence may be required in the following circumstances:\n\n• Transactions above certain value thresholds\n• Customers assessed as higher risk based on our risk assessment\n• Politically Exposed Persons (PEPs) — individuals who hold, or have held, a prominent public function in New Zealand or overseas, and their close associates and family members\n• Complex or unusually large transactions\n• Transactions with no apparent lawful purpose\n• Customers from higher-risk jurisdictions\n\nEDD may include additional identity verification, enhanced monitoring of transactions, and obtaining information about the source of funds or wealth.`
      },
      {
        id: 'entities',
        heading: '3.3 Companies, Trusts & Other Entities',
        body: `For customers that are not natural persons (individuals), we require additional information including:\n\n• Companies: Certificate of incorporation, company register extract, identification of directors, shareholders holding 25% or more, and beneficial owners\n• Trusts: Trust deed, identification of trustees, settlors, and beneficiaries (or classes of beneficiaries)\n• Partnerships: Partnership agreement and identification of all partners\n\nBeneficial owners must be identified and verified in all cases.`
      },
      {
        id: 'what-to-bring',
        heading: '4. What to Bring When You Visit',
        body: `When visiting our Bishopdale store for the first time, or when CDD has not yet been completed, please bring:\n\n1. A valid photo ID (one of the types listed above)\n2. Proof of your current residential address (dated within the last 3 months)\n3. Information about the source of funds for your purchase\n\nIf you are acting on behalf of a company or trust, please also bring the relevant entity documentation.\n\nFor online or phone transactions, we may request certified copies of these documents or use electronic identity verification methods.`
      },
      {
        id: 'ongoing',
        heading: '5. Ongoing Monitoring',
        body: `SBE conducts ongoing monitoring of customer transactions and business relationships. This includes:\n\n• Reviewing transactions to ensure they are consistent with our knowledge of the customer\n• Keeping CDD information up to date\n• Identifying transactions that are unusual or inconsistent with the customer's profile\n• Escalating and investigating potentially suspicious activity\n\nWe may contact you from time to time to update your CDD information or to seek clarification about transactions.`
      },
      {
        id: 'reporting',
        heading: '6. Suspicious Transaction Reporting',
        body: `SBE is legally required to file Suspicious Activity Reports (SARs) with the New Zealand Police Financial Intelligence Unit when we have reasonable grounds to suspect that a transaction or activity may be related to money laundering or terrorism financing.\n\nImportant: Under section 46 of the AML/CFT Act, it is an offence for us to disclose ("tip off") to any person that a SAR has been or will be made. We are therefore unable to inform you if a report has been filed in relation to your account or transactions.`
      },
      {
        id: 'records',
        heading: '7. Record Keeping',
        body: `SBE maintains records of:\n\n• All CDD information and identity verification documents\n• Transaction records, including the nature and value of each transaction\n• Suspicious activity reports and supporting documentation\n• Staff training records\n• Risk assessments and compliance programme documentation\n\nRecords are retained for a minimum of 5 years after the end of the business relationship or the date of the transaction, whichever is later, in accordance with the AML/CFT Act.`
      },
      {
        id: 'sanctions',
        heading: '8. Sanctions Compliance',
        body: `SBE screens all customers against relevant sanctions lists, including those maintained by the New Zealand government, the United Nations, and other applicable jurisdictions. We will not knowingly conduct transactions with sanctioned individuals, entities, or countries.`
      },
      {
        id: 'precious-metals-code',
        heading: '9. Global Precious Metals Code',
        body: `Southern Bullion Exchange operates with reference to the Global Precious Metals Code, which sets standards for ethics, governance, compliance, and best practice in the precious metals market. We are committed to maintaining the highest standards of integrity and transparency in all our dealings.`
      },
      {
        id: 'training',
        heading: '10. Staff Training',
        body: `All SBE staff involved in customer-facing roles or transaction processing receive regular AML/CFT training. Training covers the identification of suspicious activity, CDD procedures, reporting obligations, and the consequences of non-compliance. Training records are maintained and the programme is reviewed annually.`
      },
      {
        id: 'contact',
        heading: '11. Contact & Questions',
        body: `If you have any questions about our AML/CFT obligations or the CDD process:\n\nAML/CFT Compliance Officer\nSouthern Bullion Exchange Limited\nShop 9 Bishopdale Court\nBishopdale 8053, Christchurch\nNew Zealand\n\nPhone: 021 153 1763 / (03) 359 6060\nEmail: southernbullionnz@gmail.com\n\nFor more information about AML/CFT in New Zealand:\nDepartment of Internal Affairs — www.dia.govt.nz\nNew Zealand Police Financial Intelligence Unit — www.police.govt.nz/advice/financial-intelligence-unit`
      },
    ]
  },
};

function TableOfContents({ sections, activeId }) {
  return (
    <nav className="hidden lg:block sticky top-28" data-testid="legal-toc">
      <p className="text-xs uppercase tracking-wider font-semibold text-[#D4AF37] mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
        On this page
      </p>
      <ul className="space-y-1.5 border-l border-gray-200">
        {sections.map(s => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className={`block pl-4 py-1 text-sm transition-colors border-l-2 -ml-px ${
                activeId === s.id
                  ? 'border-[#D4AF37] text-[#D4AF37] font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {s.heading}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function renderBody(body) {
  return body.split('\n').map((line, i) => {
    const key = `${i}-${line.slice(0, 24)}`;
    if (!line.trim()) return <br key={key} />;
    if (line.startsWith('•') || line.startsWith('  —')) {
      const indent = line.startsWith('  —') ? 'ml-6' : 'ml-4';
      const bullet = line.startsWith('  —') ? '—' : '\u2022';
      const text = line.replace(/^[•\s—]+/, '').trim();
      return (
        <p key={key} className={`${indent} mb-1.5 text-gray-600 text-sm flex gap-2`} style={{ fontFamily: "'Poppins', sans-serif" }}>
          <span className="text-[#D4AF37] flex-shrink-0">{bullet}</span>
          <span>{text}</span>
        </p>
      );
    }
    if (line.match(/^\d+\.\s/)) {
      return (
        <p key={key} className="ml-4 mb-1.5 text-gray-600 text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
          {line}
        </p>
      );
    }
    return (
      <p key={key} className="mb-3 text-gray-600 text-sm leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
        {line}
      </p>
    );
  });
}

export default function LegalPage() {
  const location = useLocation();
  const page = legalContent[location.pathname] || legalContent['/terms'];
  const [activeId, setActiveId] = useState('');
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0 }
    );
    page.sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [page, location.pathname]);

  const seoDescriptions = {
    '/terms': 'Terms and Conditions of trade for Southern Bullion Exchange Ltd. Read our trading terms, payment policies, and delivery information for bullion purchases in New Zealand.',
    '/privacy': 'Privacy Policy for Southern Bullion Exchange Ltd. How we collect, use, and protect your personal information under New Zealand privacy law.',
    '/aml-cft': 'AML/CFT compliance policy for Southern Bullion Exchange Ltd. Our anti-money laundering and countering financing of terrorism obligations under NZ law.',
  };

  return (
    <div data-testid="legal-page">
      <SEO
        title={page.title}
        description={seoDescriptions[location.pathname] || `${page.title} - Southern Bullion Exchange legal documentation.`}
        path={location.pathname}
      />
      {/* Hero */}
      <section className="bg-[#0A0A0A] py-16 text-center">
        <div className="max-w-7xl mx-auto px-6">
          <p className="section-label mb-3">{page.subtitle}</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "Cinzel, serif" }} data-testid="legal-page-title">
            {page.title}
          </h1>
          <p className="text-gray-500 mt-4 text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Southern Bullion Exchange Limited &middot; Last updated: {page.lastUpdated}
          </p>
        </div>
      </section>

      {/* Page Nav */}
      <div className="bg-[#FAFAFA] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex gap-1 overflow-x-auto py-3">
            {legalPages.map(lp => {
              const active = location.pathname === lp.path;
              return (
                <Link
                  key={lp.path}
                  to={lp.path}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-sm whitespace-nowrap transition-all ${
                    active
                      ? 'bg-[#0A0A0A] text-[#D4AF37] font-semibold'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                  data-testid={`legal-nav-${lp.path.replace('/', '')}`}
                >
                  <lp.icon size={14} />
                  {lp.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-8 md:py-12 max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Sidebar TOC - hidden on mobile, shown on desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <TableOfContents sections={page.sections} activeId={activeId} />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0 max-w-3xl break-words">
            {page.sections.map((section, i) => (
              <div
                key={section.id}
                id={section.id}
                className={`${i > 0 ? 'mt-10 pt-10 border-t border-gray-100' : ''}`}
                data-testid={`legal-section-${section.id}`}
              >
                <h2 className="text-lg font-bold mb-4 text-[#0A0A0A]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {section.heading}
                </h2>
                {renderBody(section.body)}
              </div>
            ))}

            {/* Back to top */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-xs text-gray-400 text-center" style={{ fontFamily: "'Poppins', sans-serif" }}>
                &copy; {new Date().getFullYear()} Southern Bullion Exchange Limited. All rights reserved.
                <br />
                This document does not constitute legal or financial advice.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
